import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box, TextField, Typography, Paper, CircularProgress, Alert, Stack
} from '@mui/material';
import * as api from './articleTypesApi';
import type { ArticleType } from './articleTypesApi';
import { sx14, paperSx, FormRow, DetailHeader } from '../common/detailView';

interface Props {
    articleTypeId: number | null;
}

export function ArticleTypeView({ articleTypeId }: Props) {
    const { t } = useTranslation();

    const [item, setItem] = useState<ArticleType | null>(null);
    const [isEditing, setIsEditing] = useState(false);
    const [isNew, setIsNew] = useState(false);
    const [copy, setCopy] = useState<ArticleType | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            if (articleTypeId === null) {
                setItem({ id: 0, articleTypeName: '' });
                setIsNew(true);
                setIsEditing(true);
            } else {
                const data = await api.getArticleType(articleTypeId);
                setItem(data);
                setIsNew(false);
                setIsEditing(false);
            }
        } catch (err) {
            setError(String(err));
        } finally {
            setLoading(false);
        }
    }, [articleTypeId]);

    useEffect(() => { load(); }, [load]);

    if (loading) return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;
    if (error && !item) return <Alert severity="error">{error}</Alert>;
    if (!item) return null;

    const hasName = (): boolean => !!item.articleTypeName && item.articleTypeName.length > 0;

    const startEdit = () => { setCopy(JSON.parse(JSON.stringify(item))); setIsEditing(true); };
    const cancelEdit = () => { if (copy) setItem(copy); setIsEditing(false); };

    const save = async () => {
        setSaving(true); setError(null);
        try {
            const response = await api.saveArticleType(item);
            setItem(response);
            setIsNew(false);
            setIsEditing(false);
        } catch (err) {
            setError(String(err));
        } finally {
            setSaving(false);
        }
    };

    const update = <K extends keyof ArticleType>(field: K, value: ArticleType[K]) => {
        setItem({ ...item, [field]: value });
    };

    return (
        <Paper variant="outlined" sx={paperSx}>
            <DetailHeader
                title={item.name ?? item.articleTypeName}
                isEditing={isEditing}
                isNew={isNew}
                saving={saving}
                canSave={hasName()}
                onEdit={startEdit}
                onSave={save}
                onCancel={cancelEdit}
            />

            <Box p={2}>
                <Stack spacing={2}>
                    {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

                    <FormRow label={t('article.articleTypeName')}>
                        {!isEditing ? (
                            <Typography sx={sx14}>{item.articleTypeName}</Typography>
                        ) : (
                            <TextField size="small" fullWidth
                                value={item.articleTypeName}
                                onChange={e => update('articleTypeName', e.target.value)}
                                error={!hasName()} />
                        )}
                    </FormRow>

                    <FormRow label={t('article.articleSystemType')}>
                        <Typography sx={sx14}>{item.articleSystemType}</Typography>
                    </FormRow>
                </Stack>
            </Box>

            {saving && <Box display="flex" justifyContent="center" p={1}><CircularProgress size={20} /></Box>}
        </Paper>
    );
}
